import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import { invalidateAttributionCache, invalidateTagCache } from '../cacheInvalidation';
import type {
    Tag,
    CreateTagData,
    UpdateTagData,
    PaginatedResponse,
    SetTagLedgersData,
    SetTagLedgersResponse,
} from '../types';

export function useTags() {
    return useQuery({
        queryKey: ['tags'],
        queryFn: () => api.get<PaginatedResponse<Tag>>('/api/tags'),
    });
}

export function useTag(id: number) {
    return useQuery({
        queryKey: ['tags', id],
        queryFn: () => api.get<Tag>(`/api/tags/${id}`),
        enabled: !!id,
    });
}

export function useCreateTag() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: CreateTagData) => api.post<Tag>('/api/tags', data),
        onSuccess: () => {
            invalidateTagCache(queryClient);
        },
    });
}

export function useUpdateTag() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: UpdateTagData }) =>
            api.patch<Tag>(`/api/tags/${id}`, data),
        onSuccess: () => {
            invalidateTagCache(queryClient);
        },
    });
}

export function useDeleteTag() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => api.delete<void>(`/api/tags/${id}`),
        onSuccess: (_, deletedId) => {
            queryClient.removeQueries({ queryKey: ['tags', deletedId] });
            invalidateTagCache(queryClient);
        },
    });
}

/**
 * Sets a tag's complete member set via PUT /api/tags/{id}/ledgers.
 * Full-replacement semantics: ledgers not listed lose the tag,
 * `ledgerIds: []` clears the tag from all ledgers.
 */
export function useSetTagLedgers() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: SetTagLedgersData }) =>
            api.put<SetTagLedgersResponse>(`/api/tags/${id}/ledgers`, data),
        onSuccess: () => {
            invalidateAttributionCache(queryClient);
        },
    });
}
